import { isAuthenticated } from '../utils/auth';
import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

/* ---------------------- RULE CONFIGS ---------------------- */

const CATEGORY_RULES = [
  { keywords: ['python', 'sql', 'java'], category: 'Programming' },
  { keywords: ['powerbi', 'tableau', 'matplotlib'], category: 'Data Visualization' },
  { keywords: ['aws', 'azure', 'cloud'], category: 'Cloud' },
  { keywords: ['data', 'statistics'], category: 'Data Science' },
  { keywords: ['structure', 'algorithm'], category: 'Computer Science' }
];

const IMAGE_RULES = [
  { keywords: ['python'], image: 'course1.jpg' },
  { keywords: ['sql'], image: 'sqlimage.png' },
  { keywords: ['data'], image: 'datascienceandai.png' },
  { keywords: ['powerbi'], image: 'course3.jpg' },
  { keywords: ['machine learning'], image: 'machinelearning.jpg' },
  { keywords: ['aws', 'cloud'], image: 'aws.jpg' },
  { keywords: ['azure'], image: 'azure.jpg' },
  { keywords: ['gcp'], image: 'gcp.jpg' },
  { keywords: ['tableau'], image: 'tableau.jpg' },
  { keywords: ['pyspark'], image: 'pyspark.jpg' },
  { keywords: ['deeplearning'], image: 'deeplearning.jpg' },
  { keywords: ['dbt'], image: 'dbt.jpg' },
  { keywords: ['kafka'], image: 'kafka.jpg' },
  { keywords: ['r beginner', 'r intermediate', 'r advanced'], image: 'r.jpg' },
  { keywords: ['matplotlib'], image: 'matplotlib.jpg' },
  { keywords: ['airflow'], image: 'airflow.jpg' },
  { keywords: ['hadoop'], image: 'hadoop.jpg' },
  { keywords: ['pandas'], image: 'pandas.jpg' },
  { keywords: ['statistics'], image: 'statistics.png' },
  { keywords: ['salesforce'], image: 'salesforce.png' },
  { keywords: ['product', 'engineering'], image: 'productengineeringandservices.png' },
  { keywords: ['h1b', 'staff'], image: 'H1BSTAFF.png' },
  { keywords: ['erp'], image: 'erp.png' },
  { keywords: ['automation testing'], image: 'automation.jpg' },
  { keywords: ['oci beginner', 'oci intermediate', 'oci advanced'], image: 'oci.jpg' },
  { keywords: ['spring', 'springboot'], image: 'springboot.jpg' },
  { keywords: ['angular beginner', 'angular intermediate', 'angular advanced'], image: 'angular.jpg' },
  { keywords: ['django beginner', 'django intermediate', 'django advanced'], image: 'django.jpg' },
  { keywords: ['java beginner', 'java intermediate', 'java advanced'], image: 'java.jpg' },
  { keywords: ['javascript'], image: 'javascript.jpg' },
  { keywords: ['typescript'], image: 'javascript.jpg' },
  { keywords: ['kubernetes beginner', 'kubernetes intermediate', 'kubernetes advanced'], image: 'kubernetes.png' },
  { keywords: ['mulesoft beginner', 'mulesoft intermediate', 'mulesoft advanced'], image: 'mulesoft.jpg' },
  { keywords: ['react beginner', 'react intermediate', 'react advanced'], image: 'react.jpg' },
  { keywords: ['numpy'], image: 'numpy.jpg' },
  { keywords: ['enterprise', 'architecture'], image: 'entierpriseandarchitecture.png' },
  { keywords: ['ai', 'datascience'], image: 'datascienceandai.png' }
];

/* ---------------------- GENERIC MATCHER ---------------------- */

const matchByRules = (title, rules, defaultValue) => {
  if (!title) return defaultValue;
  const t = title.toLowerCase();

  for (const rule of rules) {
    if (rule.keywords.some(k => t.includes(k))) {
      return rule.category || rule.image;
    }
  }

  return defaultValue;
};

/* ---------------------- HELPERS ---------------------- */

const getCourseCategory = (title) =>
  matchByRules(title, CATEGORY_RULES, 'General');

const getCourseImage = (title) =>
  matchByRules(title, IMAGE_RULES, 'course2.jpg');

/* ---------------------- SECURE RANDOM ---------------------- */
/* Replaces Math.random() with CSPRNG without changing functionality */

const getSecureRandomInt = (min, max) => {
  const cryptoObj = window.crypto || window.msCrypto;


  if (!cryptoObj) {
    throw new Error("Secure random generator not available");
  }


  const range = max - min + 1;
  const array = new Uint32Array(1);

  cryptoObj.getRandomValues(array);

  return min + (array[0] % range);
};

/* ---------------------- MAIN HOOK ---------------------- */

const useCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated()) return;

    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await apiFetch(
          `${window._env_.API_BASE_URL}/courses/summary`
        );

        const transformedData = data.map(course => ({
          id: course.course_id,
          title: course.course_title || 'Untitled Course',
          description: course.course_description || 'No description available',
          level: course.course_level || 'Not Specified',
          department: course.department_name || 'General',
          vc: course.video_count,
          td: course.total_duration,
          category: getCourseCategory(course.course_title),

          // ✅ Secure random enrolled count (50–249)
          enrolled: getSecureRandomInt(50, 249),

          image: getCourseImage(course.course_title)
        }));

        setCourses(transformedData);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError(err.message);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  return { courses, loading, error };
};

export default useCourses;
